import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { LayoutGrid, Settings, LogOut, Plus, Pencil, Trash2, GripVertical, ChevronDown, ChevronRight, Download, Eye, Utensils, ExternalLink, Tag, ImagePlus, X, UserCheck, Bell } from "lucide-react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { QRCodeSVG } from "qrcode.react";
import api from "@/lib/api";

const fmtPrice = (p) => `$${Number(p).toLocaleString("es-CO")}`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const imgUrl = (path) => path ? `${BACKEND_URL}/api/files/${path}` : null;

function ItemVisual({ icon, imagePath, size = "text-xl", imgClass = "w-8 h-8" }) {
  if (imagePath) return <img src={imgUrl(imagePath)} alt="" className={`${imgClass} rounded-md object-cover`} />;
  return <span className={size}>{icon || "📝"}</span>;
}

export default function RestaurantDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState("menu");
  const [menuType, setMenuType] = useState("main"); // for dialogs/actions context
  const [profile, setProfile] = useState(null);
  const [mainCategories, setMainCategories] = useState([]);
  const [mainItems, setMainItems] = useState([]);
  const [hhCategories, setHhCategories] = useState([]);
  const [hhItems, setHhItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [myRestaurants, setMyRestaurants] = useState([]);
  const [selectedRid, setSelectedRid] = useState(null);
  const [expandedCats, setExpandedCats] = useState({});
  const [showItemDialog, setShowItemDialog] = useState(false);
  const [itemForm, setItemForm] = useState({ category_id: "", name: "", description: "", price: 0, icon: "", allergens: "", badges: "", image_path: "" });
  const [editItem, setEditItem] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [showCatDialog, setShowCatDialog] = useState(false);
  const [catForm, setCatForm] = useState({ name: "", icon: "", description: "" });
  const [editCat, setEditCat] = useState(null);
  const [gtmId, setGtmId] = useState("");
  const [noRestaurant, setNoRestaurant] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [stdMenus, setStdMenus] = useState([]);
  const [hhSchedule, setHhSchedule] = useState({ days: [], start_time: "", end_time: "" });
  const [showNotifDialog, setShowNotifDialog] = useState(false);
  const [editNotif, setEditNotif] = useState(null);
  const [notifForm, setNotifForm] = useState({ title: "", description: "", icon: "" });

  const rid = selectedRid;
  const ridRef = useRef(selectedRid);
  ridRef.current = selectedRid;

  const loadData = useCallback(async (forceRid) => {
    if (!forceRid) return;
    try {
      const [p, mMain, mHH, t, n, sm] = await Promise.all([
        api.restaurant.getProfile(forceRid),
        api.restaurant.getMenu(forceRid, "main"),
        api.restaurant.getMenu(forceRid, "happy_hour"),
        api.templates.list(),
        api.restaurant.getNotifications(forceRid),
        api.admin.getStandardMenus().catch(() => ({ data: [] }))
      ]);
      let finalMain = mMain.data;
      let finalHH = mHH.data;
      // Auto-sync if menus assigned but empty
      const needsSync = (p.data.main_menu_id && finalMain.categories.length === 0) ||
                         (p.data.happy_hour_menu_id && finalHH.categories.length === 0);
      if (needsSync) {
        await api.restaurant.syncAssigned(forceRid);
        const [mMain2, mHH2] = await Promise.all([
          api.restaurant.getMenu(forceRid, "main"),
          api.restaurant.getMenu(forceRid, "happy_hour")
        ]);
        finalMain = mMain2.data;
        finalHH = mHH2.data;
      }
      setProfile(p.data);
      setMainCategories(finalMain.categories);
      setMainItems(finalMain.items);
      setHhCategories(finalHH.categories);
      setHhItems(finalHH.items);
      setCategories(finalMain.categories);
      setItems(finalMain.items);
      setTemplates(t.data);
      setGtmId(p.data.gtm_container_id || "");
      setNotifications(n.data);
      setStdMenus(sm.data || []);
      setHhSchedule(p.data.happy_hour_schedule || { days: [], start_time: "", end_time: "" });
    } catch (e) {
      if (e.response?.status === 404 || e.response?.status === 403) setNoRestaurant(true);
    }
  }, []); // no dependencies - always uses forceRid

  const reload = () => loadData(ridRef.current);

  useEffect(() => {
    api.auth.me().then((r) => {
      setUser(r.data);
      if (r.data.role !== "restaurant") navigate("/login");
      api.restaurant.myRestaurants().then(res => {
        setMyRestaurants(res.data);
        if (res.data.length > 0) {
          setSelectedRid(res.data[0].id);
        } else {
          setNoRestaurant(true);
        }
      }).catch(() => setNoRestaurant(true));
    }).catch(() => navigate("/login"));
  }, [navigate]);

  useEffect(() => {
    if (selectedRid) loadData(selectedRid);
  }, [selectedRid, loadData]);

  const handleLogout = () => { localStorage.removeItem("token"); localStorage.removeItem("user"); navigate("/login"); };

  const switchRestaurant = (newRid) => {
    setSelectedRid(newRid);
    setExpandedCats({});
    setMainCategories([]);
    setMainItems([]);
    setHhCategories([]);
    setHhItems([]);
  };

  const [importMenuId, setImportMenuId] = useState("");
  const [showImportDialog, setShowImportDialog] = useState(false);

  const doImport = async () => {
    if (!importMenuId) { toast.error("Selecciona un menu estandar"); return; }
    if (!window.confirm(`Esto reemplazara el menu ${menuType === "happy_hour" ? "Happy Hour" : "Principal"} actual. Continuar?`)) return;
    try {
      await api.restaurant.importMenu({ menu_id: importMenuId, menu_type: menuType, import_mode: "full" }, rid);
      toast.success("Menu importado");
      setShowImportDialog(false);
      reload();
    } catch (e) { toast.error("Error al importar"); }
  };

  const importStandard = async () => {
    if (!window.confirm("Esto reemplazara tu menu actual con el menu estandar. Continuar?")) return;
    try {
      await api.restaurant.importStandard(rid);
      toast.success("Menu estandar importado");
      reload();
    } catch (e) { toast.error("Error al importar"); }
  };

  const selectTemplate = async (slug) => {
    try {
      await api.restaurant.updateProfile({ template_slug: slug }, rid);
      setProfile(prev => ({ ...prev, template_slug: slug }));
      toast.success("Plantilla seleccionada");
    } catch (e) { toast.error("Error"); }
  };

  const saveGtm = async () => {
    try {
      await api.restaurant.updateProfile({ gtm_container_id: gtmId }, rid);
      toast.success("Google Tag Manager guardado");
    } catch (e) { toast.error("Error"); }
  };

  // Override functions (no CRUD - categories/items come from standard menu)
  const openEditItem = (item, mt) => {
    setEditItem(item);
    setMenuType(mt);
    setItemForm({ category_id: item.category_id, name: item.name, description: item.description || "",
      price: item.price, icon: item.icon || "", allergens: (item.allergens || []).join(", "), badges: (item.badges || []).join(", "), image_path: item.image_path || "" });
    setShowItemDialog(true);
  };
  const saveItemPrice = async () => {
    if (!editItem) return;
    try {
      await api.restaurant.updateOverride(
        { target_type: "item", target_id: editItem.id, price: Number(itemForm.price) },
        rid, menuType
      );
      toast.success("Precio actualizado");
      setShowItemDialog(false);
      reload();
    } catch (e) { toast.error("Error"); }
  };

  const toggleVisibility = async (type, id, visible, mt) => {
    await api.restaurant.updateOverride(
      { target_type: type === "category" ? "category" : "item", target_id: id, is_visible: visible },
      rid, mt
    );
    reload();
  };

  // Drag & Drop for reordering sections and items
  const handleMenuDragEnd = async (result, mt, cats, setCats, itms, setItms) => {
    const { source, destination, type } = result;
    if (!destination) return;
    if (type === "CATEGORY") {
      const newCats = Array.from(cats);
      const [moved] = newCats.splice(source.index, 1);
      newCats.splice(destination.index, 0, moved);
      const reordered = newCats.map((c, i) => ({ ...c, order: i }));
      setCats(reordered);
      await api.restaurant.reorderMenu(
        { items: reordered.map(c => ({ target_type: "category", target_id: c.id, order: c.order })) }, rid, mt
      );
    } else if (type === "ITEM" && source.droppableId === destination.droppableId) {
      const catId = source.droppableId;
      const catItems = itms.filter(i => i.category_id === catId).sort((a, b) => (a.order||0) - (b.order||0));
      const [moved] = catItems.splice(source.index, 1);
      catItems.splice(destination.index, 0, moved);
      const reordered = catItems.map((item, i) => ({ ...item, order: i }));
      setItms(prev => [...prev.filter(i => i.category_id !== catId), ...reordered]);
      const itemKey = (item) => item.product_id || item.id;
      await api.restaurant.reorderMenu(
        { items: reordered.map(i => ({ target_type: "item", target_id: itemKey(i), order: i.order })) }, rid, mt
      );
    }
  };

  const toggleCat = (id) => setExpandedCats(prev => ({ ...prev, [id]: !prev[id] }));

  // Notification CRUD
  const openNewNotif = () => { setEditNotif(null); setNotifForm({ title: "", description: "", icon: "📢" }); setShowNotifDialog(true); };
  const openEditNotif = (n) => { setEditNotif(n); setNotifForm({ title: n.title, description: n.description || "", icon: n.icon || "" }); setShowNotifDialog(true); };
  const saveNotif = async () => {
    try {
      if (editNotif) await api.restaurant.updateNotification(editNotif.id, notifForm, rid);
      else await api.restaurant.createNotification(notifForm, rid);
      toast.success(editNotif ? "Actualizada" : "Creada");
      setShowNotifDialog(false);
      reload();
    } catch (e) { toast.error("Error"); }
  };
  const deleteNotif = async (id) => {
    if (!window.confirm("Eliminar notificacion?")) return;
    await api.restaurant.deleteNotification(id, rid);
    reload();
  };
  const toggleNotifActive = async (id, active) => {
    await api.restaurant.updateNotification(id, { is_active: active }, rid);
    reload();
  };

  const sidebarItems = [
    { id: "menu", label: "Mi Menu", icon: LayoutGrid },
    { id: "notifications", label: "Notificaciónes", icon: Bell },
    { id: "settings", label: "Configuración", icon: Settings },
  ];

  if (!user) return null;

  const isImpersonating = !!localStorage.getItem("admin_token");
  const backToAdmin = () => {
    const adminToken = localStorage.getItem("admin_token");
    const adminUser = localStorage.getItem("admin_user");
    if (adminToken) {
      localStorage.setItem("token", adminToken);
      localStorage.removeItem("admin_token");
    }
    if (adminUser) {
      localStorage.setItem("user", adminUser);
      localStorage.removeItem("admin_user");
    }
    navigate("/admin");
  };

  if (noRestaurant) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#FDFBF7' }}>
      <div className="text-center max-w-md">
        <Utensils className="h-16 w-16 mx-auto mb-4" style={{ color: '#C05C46' }} />
        <h2 className="text-2xl font-heading font-bold mb-2" style={{ color: '#2A2A2A' }}>Sin restaurante asignado</h2>
        <p className="font-body mb-4" style={{ color: '#6B6A65' }}>Este usuario no tiene un restaurante asignado.</p>
        {isImpersonating ? (
          <Button onClick={backToAdmin} style={{ background: '#C05C46' }} data-testid="back-to-admin-btn">Volver al Admin</Button>
        ) : (
          <Button onClick={handleLogout} variant="outline">Cerrar Sesion</Button>
        )}
      </div>
    </div>
  );

  return (
    <div className="dashboard-layout" data-testid="restaurant-dashboard">
      {/* Impersonation banner */}
      {isImpersonating && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, zIndex: 50,
          background: "#C05C46", color: "white", padding: "8px 16px",
          display: "flex", alignItems: "center", justifyContent: "center", gap: "12px",
          fontSize: "0.85rem", fontWeight: 600
        }} data-testid="impersonation-banner">
          <UserCheck className="h-4 w-4" />
          Viendo como: {profile?.name || user?.email}
          <button onClick={backToAdmin}
            style={{ background: "rgba(255,255,255,0.2)", border: "none", color: "white",
              padding: "4px 14px", borderRadius: "6px", cursor: "pointer", fontSize: "0.8rem", fontWeight: 600 }}
            data-testid="back-to-admin-btn">
            Volver al Admin
          </button>
        </div>
      )}
      <aside className="dashboard-sidebar" style={{ paddingTop: isImpersonating ? "56px" : "24px" }}>
        <div className="flex items-center gap-3 mb-4 px-2">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: '#C05C46' }}>
            <Utensils className="h-5 w-5 text-white" />
          </div>
          <div>
            <div className="font-heading font-bold text-sm truncate" style={{ color: '#2A2A2A' }}>{profile?.name || "Restaurante"}</div>
            <div className="text-xs" style={{ color: '#6B6A65' }}>Panel</div>
          </div>
        </div>
        {/* Restaurant Selector */}
        {myRestaurants.length > 1 && (
          <div className="mb-6 px-2">
            <select value={selectedRid || ""} onChange={e => switchRestaurant(e.target.value)}
              className="w-full text-sm p-2 rounded-md border font-body"
              style={{ borderColor: '#E6E4DD', background: '#FDFBF7', color: '#2A2A2A' }}
              data-testid="restaurant-selector">
              {myRestaurants.map(r => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
          </div>
        )}
        <nav className="flex-1">
          {sidebarItems.map(s => (
            <div key={s.id} className={`sidebar-nav-item ${tab === s.id ? "active" : ""}`}
              onClick={() => setTab(s.id)} data-testid={`nav-${s.id}`}>
              <s.icon className="h-4 w-4" /> {s.label}
            </div>
          ))}
          {profile?.slug && (
            <a href={`/menu/${profile.slug}`} target="_blank" rel="noreferrer"
              className="sidebar-nav-item" data-testid="preview-menu-link">
              <Eye className="h-4 w-4" /> Ver Menu Publico
            </a>
          )}
        </nav>
        <div className="border-t pt-4 mt-4" style={{ borderColor: '#E6E4DD' }}>
          <div className="text-xs mb-2 px-2 truncate" style={{ color: '#6B6A65' }}>{user?.email}</div>
          <div className="sidebar-nav-item" onClick={handleLogout} data-testid="logout-btn">
            <LogOut className="h-4 w-4" /> Cerrar Sesion
          </div>
        </div>
      </aside>

      <main className="dashboard-main">
        {/* Menu Builder */}
        {tab === "menu" && (
          <div className="animate-fade-in">
            <h2 className="text-2xl font-heading font-bold mb-6" style={{ color: '#2A2A2A' }}>Constructor de Menu</h2>

            {/* ===== MENU PRINCIPAL ===== */}
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="px-3 py-1 rounded-lg text-sm font-bold" style={{ background: '#C05C46', color: 'white' }}>Menu Principal</div>
                <span className="text-xs" style={{ color: '#6B6A65' }}>
                  {mainCategories.length} secciones, {mainItems.length} items
                  {!profile?.main_menu_id && " — Sin menu asignado"}
                </span>
              </div>
              {!profile?.main_menu_id ? (
                <div className="text-center py-10 bg-white border rounded-lg" style={{ borderColor: '#E6E4DD' }}>
                  <LayoutGrid className="h-10 w-10 mx-auto mb-3" style={{ color: '#A89F9A' }} />
                  <p className="font-body text-sm" style={{ color: '#6B6A65' }}>El administrador debe asignar un menu principal a este restaurante.</p>
                </div>
              ) : (
                <DragDropContext onDragEnd={(r) => handleMenuDragEnd(r, "main", mainCategories, setMainCategories, mainItems, setMainItems)}>
                  <Droppable droppableId="main-cats" type="CATEGORY">
                    {(provided) => (
                      <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-2">
                        {mainCategories.map((cat, idx) => (
                          <Draggable key={cat.id} draggableId={`mc-${cat.id}`} index={idx}>
                            {(provided, snapshot) => (
                              <div ref={provided.innerRef} {...provided.draggableProps}
                                className={`bg-white border rounded-lg ${snapshot.isDragging ? "shadow-lg" : ""}`}
                                style={{ ...provided.draggableProps.style, borderColor: '#E6E4DD', opacity: cat.is_visible === false ? 0.5 : 1 }}>
                                <div className="flex items-center gap-3 p-3">
                                  <div {...provided.dragHandleProps} className="drag-handle"><GripVertical className="h-4 w-4" style={{ color: '#A89F9A' }} /></div>
                                  <span className="text-xl">{cat.icon}</span>
                                  <div className="flex-1 cursor-pointer" onClick={() => toggleCat(cat.id)}>
                                    <div className="font-heading font-bold text-sm" style={{ color: '#2A2A2A' }}>{cat.name}</div>
                                    <div className="text-xs" style={{ color: '#6B6A65' }}>{mainItems.filter(i => i.category_id === cat.id).length} items</div>
                                  </div>
                                  <Switch checked={cat.is_visible !== false} onCheckedChange={(v) => toggleVisibility("category", cat.id, v, "main")} />
                                  {expandedCats[cat.id] ? <ChevronDown className="h-4 w-4" style={{ color: '#6B6A65' }} /> : <ChevronRight className="h-4 w-4" style={{ color: '#6B6A65' }} />}
                                </div>
                                {expandedCats[cat.id] && (
                                  <Droppable droppableId={cat.id} type="ITEM">
                                    {(provided) => (
                                      <div ref={provided.innerRef} {...provided.droppableProps} className="px-3 pb-3 space-y-1">
                                        {mainItems.filter(i => i.category_id === cat.id).sort((a, b) => (a.order||0) - (b.order||0)).map((item, iIdx) => (
                                          <Draggable key={item.id} draggableId={`mi-${item.id}`} index={iIdx}>
                                            {(provided, snapshot) => (
                                              <div ref={provided.innerRef} {...provided.draggableProps}
                                                className={`flex items-center gap-3 p-2 rounded-md border ${snapshot.isDragging ? "shadow-md" : ""}`}
                                                style={{ ...provided.draggableProps.style, borderColor: '#E6E4DD', background: '#FDFBF7', opacity: item.is_visible === false ? 0.4 : 1 }}>
                                                <div {...provided.dragHandleProps} className="drag-handle"><GripVertical className="h-3 w-3" style={{ color: '#A89F9A' }} /></div>
                                                <ItemVisual icon={item.icon} imagePath={item.image_path} />
                                                <div className="flex-1 min-w-0">
                                                  <div className="flex justify-between items-center">
                                                    <span className="font-medium text-sm truncate" style={{ color: '#2A2A2A' }}>{item.name}</span>
                                                    <span className="font-bold text-sm ml-2 whitespace-nowrap" style={{ color: '#C05C46' }}>{fmtPrice(item.price)}</span>
                                                  </div>
                                                </div>
                                                <Switch checked={item.is_visible !== false} onCheckedChange={(v) => toggleVisibility("item", item.product_id || item.id, v, "main")} />
                                              </div>
                                            )}
                                          </Draggable>
                                        ))}
                                        {provided.placeholder}
                                      </div>
                                    )}
                                  </Droppable>
                                )}
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              )}
            </div>

            {/* ===== HAPPY HOUR ===== */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="px-3 py-1 rounded-lg text-sm font-bold" style={{ background: '#D4772C', color: 'white' }}>Happy Hour</div>
                <span className="text-xs" style={{ color: '#6B6A65' }}>
                  {hhCategories.length} secciones, {hhItems.length} items
                  {!profile?.happy_hour_menu_id && " — Sin menu asignado"}
                </span>
              </div>
              {!profile?.happy_hour_menu_id ? (
                <div className="text-center py-10 bg-white border rounded-lg" style={{ borderColor: '#E6E4DD' }}>
                  <LayoutGrid className="h-10 w-10 mx-auto mb-3" style={{ color: '#A89F9A' }} />
                  <p className="font-body text-sm" style={{ color: '#6B6A65' }}>El administrador debe asignar un menu Happy Hour a este restaurante.</p>
                </div>
              ) : (
                <DragDropContext onDragEnd={(r) => handleMenuDragEnd(r, "happy_hour", hhCategories, setHhCategories, hhItems, setHhItems)}>
                  <Droppable droppableId="hh-cats" type="CATEGORY">
                    {(provided) => (
                      <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-2">
                        {hhCategories.map((cat, idx) => (
                          <Draggable key={cat.id} draggableId={`hc-${cat.id}`} index={idx}>
                            {(provided, snapshot) => (
                              <div ref={provided.innerRef} {...provided.draggableProps}
                                className={`bg-white border rounded-lg ${snapshot.isDragging ? "shadow-lg" : ""}`}
                                style={{ ...provided.draggableProps.style, borderColor: '#E6E4DD', opacity: cat.is_visible === false ? 0.5 : 1 }}>
                                <div className="flex items-center gap-3 p-3">
                                  <div {...provided.dragHandleProps} className="drag-handle"><GripVertical className="h-4 w-4" style={{ color: '#A89F9A' }} /></div>
                                  <span className="text-xl">{cat.icon}</span>
                                  <div className="flex-1 cursor-pointer" onClick={() => toggleCat(`hh-${cat.id}`)}>
                                    <div className="font-heading font-bold text-sm" style={{ color: '#2A2A2A' }}>{cat.name}</div>
                                    <div className="text-xs" style={{ color: '#6B6A65' }}>{hhItems.filter(i => i.category_id === cat.id).length} items</div>
                                  </div>
                                  <Switch checked={cat.is_visible !== false} onCheckedChange={(v) => toggleVisibility("category", cat.id, v, "happy_hour")} />
                                  {expandedCats[`hh-${cat.id}`] ? <ChevronDown className="h-4 w-4" style={{ color: '#6B6A65' }} /> : <ChevronRight className="h-4 w-4" style={{ color: '#6B6A65' }} />}
                                </div>
                                {expandedCats[`hh-${cat.id}`] && (
                                  <Droppable droppableId={cat.id} type="ITEM">
                                    {(provided) => (
                                      <div ref={provided.innerRef} {...provided.droppableProps} className="px-3 pb-3 space-y-1">
                                        {hhItems.filter(i => i.category_id === cat.id).sort((a, b) => (a.order||0) - (b.order||0)).map((item, iIdx) => (
                                          <Draggable key={item.id} draggableId={`hi-${item.id}`} index={iIdx}>
                                            {(provided, snapshot) => (
                                              <div ref={provided.innerRef} {...provided.draggableProps}
                                                className={`flex items-center gap-3 p-2 rounded-md border ${snapshot.isDragging ? "shadow-md" : ""}`}
                                                style={{ ...provided.draggableProps.style, borderColor: '#E6E4DD', background: '#FDFBF7', opacity: item.is_visible === false ? 0.4 : 1 }}>
                                                <div {...provided.dragHandleProps} className="drag-handle"><GripVertical className="h-3 w-3" style={{ color: '#A89F9A' }} /></div>
                                                <ItemVisual icon={item.icon} imagePath={item.image_path} />
                                                <div className="flex-1 min-w-0">
                                                  <div className="flex justify-between items-center">
                                                    <span className="font-medium text-sm truncate" style={{ color: '#2A2A2A' }}>{item.name}</span>
                                                    <span className="font-bold text-sm ml-2 whitespace-nowrap" style={{ color: '#D4772C' }}>{fmtPrice(item.price)}</span>
                                                  </div>
                                                </div>
                                                <Switch checked={item.is_visible !== false} onCheckedChange={(v) => toggleVisibility("item", item.product_id || item.id, v, "happy_hour")} />
                                              </div>
                                            )}
                                          </Draggable>
                                        ))}
                                        {provided.placeholder}
                                      </div>
                                    )}
                                  </Droppable>
                                )}
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              )}
            </div>
          </div>
        )}

        {/* Notifications Tab */}
        {tab === "notifications" && (
          <div className="animate-fade-in">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-heading font-bold" style={{ color: '#2A2A2A' }}>Notificaciónes</h2>
              <Button onClick={openNewNotif} style={{ background: '#C05C46' }} data-testid="add-notif-btn">
                <Plus className="h-4 w-4 mr-2" /> Nueva Notificación
              </Button>
            </div>
            <p className="text-sm mb-6" style={{ color: '#6B6A65' }}>
              Las notificaciones aparecen en el menu publico cuando el cliente toca el icono de campana. Usa promociones, avisos o informacion importante.
            </p>
            <div className="space-y-3">
              {notifications.map(n => (
                <div key={n.id} className="flex items-center gap-4 p-4 bg-white border rounded-lg"
                  style={{ borderColor: '#E6E4DD', opacity: n.is_active ? 1 : 0.5 }}>
                  <span className="text-2xl">{n.icon || "📢"}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-bold text-sm" style={{ color: '#2A2A2A' }}>{n.title}</div>
                    <div className="text-xs" style={{ color: '#6B6A65' }}>{n.description}</div>
                  </div>
                  <Switch checked={n.is_active !== false} onCheckedChange={(v) => toggleNotifActive(n.id, v)} />
                  <Button size="sm" variant="ghost" onClick={() => openEditNotif(n)}><Pencil className="h-3 w-3" /></Button>
                  <Button size="sm" variant="ghost" className="text-red-500" onClick={() => deleteNotif(n.id)}><Trash2 className="h-3 w-3" /></Button>
                </div>
              ))}
              {notifications.length === 0 && (
                <div className="text-center py-12 bg-white border rounded-lg" style={{ borderColor: '#E6E4DD' }}>
                  <Bell className="h-10 w-10 mx-auto mb-3" style={{ color: '#A89F9A' }} />
                  <p className="font-body" style={{ color: '#6B6A65' }}>No hay notificaciones. Crea tu primera promocion o aviso.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Settings Tab */}
        {tab === "settings" && (
          <div className="animate-fade-in space-y-8">
            <h2 className="text-2xl font-heading font-bold" style={{ color: '#2A2A2A' }}>Configuración</h2>

            {/* Logo Upload */}
            <div className="bg-white border rounded-lg p-6" style={{ borderColor: '#E6E4DD' }}>
              <h3 className="font-heading font-bold mb-4" style={{ color: '#2A2A2A' }}>Logo del Restaurante</h3>
              <p className="text-sm mb-4" style={{ color: '#6B6A65' }}>Este logo aparecera en la parte superior del menu publico.</p>
              <div className="flex items-center gap-4">
                {profile?.logo_url ? (
                  <div className="relative">
                    <img src={imgUrl(profile.logo_url)} alt="Logo" className="w-24 h-24 rounded-xl object-contain border" style={{ borderColor: '#E6E4DD' }} />
                    <button onClick={async () => { await api.restaurant.updateProfile({ logo_url: "" }, rid); reload(); }}
                      className="absolute -top-2 -right-2 w-5 h-5 rounded-full flex items-center justify-center bg-red-500 text-white">
                      <X className="h-3 w-3" /></button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center w-24 h-24 rounded-xl border-2 border-dashed cursor-pointer hover:border-[#C05C46] transition-colors"
                    style={{ borderColor: '#E6E4DD' }} data-testid="upload-logo-label">
                    <ImagePlus className="h-8 w-8" style={{ color: '#A89F9A' }} />
                    <span className="text-[10px] mt-1" style={{ color: '#6B6A65' }}>Subir logo</span>
                    <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const fd = new FormData(); fd.append("file", file);
                      try {
                        const { data } = await api.upload(fd);
                        await api.restaurant.updateProfile({ logo_url: data.path }, rid);
                        toast.success("Logo subido");
                        reload();
                      } catch (err) { toast.error("Error al subir logo"); }
                    }} data-testid="upload-logo-input" />
                  </label>
                )}
              </div>
            </div>

            {/* Color Palette & Font */}
            <div className="bg-white border rounded-lg p-6" style={{ borderColor: '#E6E4DD' }}>
              <h3 className="font-heading font-bold mb-4" style={{ color: '#2A2A2A' }}>Paleta de Colores y Fuente</h3>
              <p className="text-sm mb-4" style={{ color: '#6B6A65' }}>Personaliza los colores y la fuente de tu menu publico.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                <div className="space-y-2">
                  <Label className="text-xs">Color principal</Label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={profile?.primary_color || "#3E1F0D"}
                      onChange={e => setProfile(p => ({...p, primary_color: e.target.value}))}
                      className="w-10 h-10 rounded border cursor-pointer" style={{ borderColor: '#E6E4DD' }} />
                    <Input value={profile?.primary_color || "#3E1F0D"} onChange={e => setProfile(p => ({...p, primary_color: e.target.value}))}
                      className="flex-1 text-xs" data-testid="color-primary" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Color acento</Label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={profile?.accent_color || "#D4772C"}
                      onChange={e => setProfile(p => ({...p, accent_color: e.target.value}))}
                      className="w-10 h-10 rounded border cursor-pointer" style={{ borderColor: '#E6E4DD' }} />
                    <Input value={profile?.accent_color || "#D4772C"} onChange={e => setProfile(p => ({...p, accent_color: e.target.value}))}
                      className="flex-1 text-xs" data-testid="color-accent" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Fondo</Label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={profile?.bg_color || "#F5EFE0"}
                      onChange={e => setProfile(p => ({...p, bg_color: e.target.value}))}
                      className="w-10 h-10 rounded border cursor-pointer" style={{ borderColor: '#E6E4DD' }} />
                    <Input value={profile?.bg_color || "#F5EFE0"} onChange={e => setProfile(p => ({...p, bg_color: e.target.value}))}
                      className="flex-1 text-xs" data-testid="color-bg" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Fuente</Label>
                  <select value={profile?.font_family || "Manrope"}
                    onChange={e => setProfile(p => ({...p, font_family: e.target.value}))}
                    className="w-full text-sm p-2 rounded-md border" style={{ borderColor: '#E6E4DD' }} data-testid="font-select">
                    <option value="Manrope">Manrope</option>
                    <option value="Inter">Inter</option>
                    <option value="Lato">Lato</option>
                    <option value="Open Sans">Open Sans</option>
                    <option value="Poppins">Poppins</option>
                    <option value="Playfair Display">Playfair Display</option>
                    <option value="Cormorant Garamond">Cormorant Garamond</option>
                    <option value="Montserrat">Montserrat</option>
                  </select>
                </div>
              </div>
              <Button onClick={async () => {
                try {
                  await api.restaurant.updateProfile({
                    primary_color: profile?.primary_color, accent_color: profile?.accent_color,
                    bg_color: profile?.bg_color, font_family: profile?.font_family
                  }, rid);
                  toast.success("Colores y fuente guardados");
                } catch (e) { toast.error("Error"); }
              }} style={{ background: '#C05C46' }} data-testid="save-colors-btn">Guardar Colores y Fuente</Button>
            </div>

            {/* Template Selector */}
            <div>
              <h3 className="text-lg font-heading font-bold mb-4" style={{ color: '#2A2A2A' }}>Plantilla del Menu</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {templates.map(t => (
                  <div key={t.id}
                    className={`border-2 rounded-lg overflow-hidden cursor-pointer transition-all hover:-translate-y-1`}
                    style={{ borderColor: profile?.template_slug === t.slug ? '#C05C46' : '#E6E4DD' }}
                    onClick={() => selectTemplate(t.slug)}
                    data-testid={`template-${t.slug}`}>
                    <img src={t.preview_image} alt={t.name} className="w-full h-48 object-cover" />
                    <div className="p-4 bg-white">
                      <div className="flex justify-between items-center">
                        <div>
                          <div className="font-heading font-bold" style={{ color: '#2A2A2A' }}>{t.name}</div>
                          <div className="text-sm" style={{ color: '#6B6A65' }}>{t.description}</div>
                        </div>
                        {profile?.template_slug === t.slug && (
                          <span className="text-xs font-bold px-3 py-1 rounded-full" style={{ background: '#C05C4620', color: '#C05C46' }}>Activa</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Happy Hour Schedule */}
            <div className="bg-white border rounded-lg p-6" style={{ borderColor: '#E6E4DD' }}>
              <h3 className="font-heading font-bold mb-2" style={{ color: '#2A2A2A' }}>Horario Happy Hour</h3>
              <p className="text-sm mb-4" style={{ color: '#6B6A65' }}>
                Configura cuando se activa automaticamente el menu Happy Hour para tus clientes.
              </p>
              <div className="space-y-4">
                <div>
                  <Label className="text-xs mb-2 block">Dias activos</Label>
                  <div className="flex gap-2 flex-wrap">
                    {[{k:"lun",l:"Lun"},{k:"mar",l:"Mar"},{k:"mie",l:"Mie"},{k:"jue",l:"Jue"},{k:"vie",l:"Vie"},{k:"sab",l:"Sab"},{k:"dom",l:"Dom"}].map(d => (
                      <button key={d.k} onClick={() => {
                        const days = hhSchedule.days || [];
                        setHhSchedule(p => ({...p, days: days.includes(d.k) ? days.filter(x => x !== d.k) : [...days, d.k]}));
                      }}
                        className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
                        style={{
                          background: (hhSchedule.days || []).includes(d.k) ? '#C05C46' : 'white',
                          color: (hhSchedule.days || []).includes(d.k) ? 'white' : '#6B6A65',
                          border: `1px solid ${(hhSchedule.days || []).includes(d.k) ? '#C05C46' : '#E6E4DD'}`
                        }}
                        data-testid={`hh-day-${d.k}`}>{d.l}</button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs">Hora inicio</Label>
                    <Input type="time" value={hhSchedule.start_time || ""} onChange={e => setHhSchedule(p => ({...p, start_time: e.target.value}))} data-testid="hh-start" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Hora fin</Label>
                    <Input type="time" value={hhSchedule.end_time || ""} onChange={e => setHhSchedule(p => ({...p, end_time: e.target.value}))} data-testid="hh-end" />
                  </div>
                </div>
                <Button onClick={async () => {
                  try {
                    await api.restaurant.updateProfile({ happy_hour_schedule: hhSchedule }, rid);
                    toast.success("Horario Happy Hour guardado");
                  } catch (e) { toast.error("Error"); }
                }} style={{ background: '#C05C46' }} data-testid="save-hh-btn">Guardar Horario</Button>
              </div>
            </div>

            {/* GTM Config */}
            <div className="bg-white border rounded-lg p-6" style={{ borderColor: '#E6E4DD' }}>
              <div className="flex items-center gap-3 mb-4">
                <Tag className="h-5 w-5" style={{ color: '#C05C46' }} />
                <h3 className="font-heading font-bold" style={{ color: '#2A2A2A' }}>Google Tag Manager</h3>
              </div>
              <p className="text-sm mb-4" style={{ color: '#6B6A65' }}>
                Ingresa tu Container ID de GTM para agregar analytics a tu menu publico.
              </p>
              <div className="flex gap-3">
                <Input value={gtmId} onChange={e => setGtmId(e.target.value)} placeholder="GTM-XXXXXXX"
                  className="max-w-xs" data-testid="gtm-input" />
                <Button onClick={saveGtm} style={{ background: '#C05C46' }} data-testid="save-gtm-btn">Guardar</Button>
              </div>
            </div>

            {/* SEO Indexing */}
            <div className="bg-white border rounded-lg p-6" style={{ borderColor: '#E6E4DD' }}>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-heading font-bold" style={{ color: '#2A2A2A' }}>Indexación en Buscadores (SEO)</h3>
                  <p className="text-sm mt-1" style={{ color: '#6B6A65' }}>
                    Permite que Google y otros buscadores encuentren tu menu. Si esta desactivado, tu menu solo sera accesible con el enlace directo.
                  </p>
                </div>
                <Switch checked={profile?.seo_index !== false}
                  onCheckedChange={async (v) => {
                    await api.restaurant.updateProfile({ seo_index: v }, rid);
                    setProfile(p => ({...p, seo_index: v}));
                    toast.success(v ? "Menu indexable en buscadores" : "Menu oculto de buscadores");
                  }}
                  data-testid="seo-index-toggle" />
              </div>
            </div>

            {/* Menu Link & QR Code */}
            {profile?.slug && (
              <div className="bg-white border rounded-lg p-6" style={{ borderColor: '#E6E4DD' }}>
                <h3 className="font-heading font-bold mb-2" style={{ color: '#2A2A2A' }}>Enlace y Codigo QR</h3>
                <p className="text-sm mb-4" style={{ color: '#6B6A65' }}>Comparte el enlace o descarga el codigo QR para colocar en las mesas de tu restaurante.</p>
                <div className="flex items-center gap-3 mb-6">
                  <Input readOnly value={`${window.location.origin}/menu/${profile.slug}`} className="flex-1" data-testid="menu-link-input" />
                  <Button variant="outline" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/menu/${profile.slug}`); toast.success("Enlace copiado"); }}
                    data-testid="copy-link-btn">
                    Copiar
                  </Button>
                  <a href={`/menu/${profile.slug}`} target="_blank" rel="noreferrer">
                    <Button variant="outline"><ExternalLink className="h-4 w-4" /></Button>
                  </a>
                </div>
                {/* QR Code */}
                <div className="flex flex-col items-center gap-4">
                  <div id="qr-container" style={{ background: "white", padding: "20px", borderRadius: "16px", border: "1px solid #E6E4DD" }}>
                    <QRCodeSVG
                      value={`${window.location.origin}/menu/${profile.slug}`}
                      size={220}
                      level="H"
                      bgColor="white"
                      fgColor={profile?.primary_color || "#3E1F0D"}
                      imageSettings={profile?.logo_url ? {
                        src: imgUrl(profile.logo_url),
                        width: 50,
                        height: 50,
                        excavate: true,
                      } : undefined}
                    />
                    {!profile?.logo_url && (
                      <div style={{
                        position: "relative", marginTop: "-135px", marginBottom: "85px",
                        display: "flex", justifyContent: "center"
                      }}>
                        <div style={{
                          width: "50px", height: "50px", borderRadius: "10px",
                          background: profile?.primary_color || "#3E1F0D",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          color: "white", fontWeight: 800, fontSize: "1.2rem",
                          fontFamily: "'Cormorant Garamond', serif", border: "3px solid white"
                        }}>
                          {(profile?.name || "R").substring(0, 2).toUpperCase()}
                        </div>
                      </div>
                    )}
                  </div>
                  <Button variant="outline" onClick={() => {
                    const svg = document.querySelector('#qr-container svg');
                    if (!svg) return;
                    const svgData = new XMLSerializer().serializeToString(svg);
                    const canvas = document.createElement('canvas');
                    canvas.width = 600; canvas.height = 600;
                    const ctx = canvas.getContext('2d');
                    ctx.fillStyle = 'white';
                    ctx.fillRect(0, 0, 600, 600);
                    const img = new Image();
                    img.onload = () => {
                      ctx.drawImage(img, 40, 40, 520, 520);
                      const a = document.createElement('a');
                      a.download = `qr-${profile.slug}.png`;
                      a.href = canvas.toDataURL('image/png');
                      a.click();
                    };
                    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
                  }} data-testid="download-qr-btn">
                    <Download className="h-4 w-4 mr-2" /> Descargar QR
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Item Price Override Dialog */}
      <Dialog open={showItemDialog} onOpenChange={setShowItemDialog}>
        <DialogContent data-testid="item-dialog">
          <DialogHeader><DialogTitle className="font-heading">Ajustar Precio</DialogTitle></DialogHeader>
          {editItem && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 rounded-lg" style={{ background: '#FDFBF7', border: '1px solid #E6E4DD' }}>
                <span className="text-2xl">{editItem.icon || "🍽️"}</span>
                <div>
                  <div className="font-bold text-sm" style={{ color: '#2A2A2A' }}>{editItem.name}</div>
                  <div className="text-xs" style={{ color: '#6B6A65' }}>{editItem.description}</div>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between p-3 rounded-lg" style={{ background: '#F5EFE0', border: '1px solid #E6E4DD' }}>
                  <span className="text-xs font-semibold" style={{ color: '#6B6A65' }}>Precio base del menu estandar</span>
                  <span className="font-bold text-sm" style={{ color: '#2A2A2A' }}>{fmtPrice(editItem.base_price ?? editItem.price)}</span>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Precio personalizado (COP)</Label>
                <Input type="number" value={itemForm.price} onChange={e => setItemForm(p => ({...p, price: e.target.value}))} data-testid="item-price-input" />
                <p className="text-xs" style={{ color: '#6B6A65' }}>Este precio solo aplica para tu restaurante. El menu estandar no se modifica.</p>
              </div>
            </div>
          )}
          <DialogFooter><Button onClick={saveItemPrice} style={{ background: '#C05C46' }} data-testid="save-item-btn">Guardar Precio</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Notification Dialog */}
      <Dialog open={showNotifDialog} onOpenChange={setShowNotifDialog}>
        <DialogContent data-testid="notif-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editNotif ? "Editar Notificación" : "Nueva Notificación"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Titulo</Label><Input value={notifForm.title} onChange={e => setNotifForm(p => ({...p, title: e.target.value}))} placeholder="Promo del Dia: 2x1 en Bebidas" data-testid="notif-title-input" /></div>
            <div className="space-y-2"><Label>Descripcion</Label><Input value={notifForm.description} onChange={e => setNotifForm(p => ({...p, description: e.target.value}))} placeholder="Valido de 18:00 a 20:00 hrs." /></div>
            <div className="space-y-2"><Label>Icono (emoji)</Label><Input value={notifForm.icon} onChange={e => setNotifForm(p => ({...p, icon: e.target.value}))} placeholder="⭐" /></div>
          </div>
          <DialogFooter><Button onClick={saveNotif} style={{ background: '#C05C46' }} data-testid="save-notif-btn">{editNotif ? "Guardar" : "Crear"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
